/** Minimal slice of the Supabase auth client used by admin requests. */
export interface SessionSource {
  getSession(): Promise<{
    data: { session: { access_token?: string } | null } | null;
    error?: unknown;
  }>;
  refreshSession(): Promise<{
    data: { session: { access_token?: string } | null } | null;
    error?: unknown;
  }>;
}

export type FetchLike = (url: string, init?: RequestInit) => Promise<Response>;
export type AdminRequest = (url: string, options?: RequestInit) => Promise<Response>;

/**
 * Wraps `fetch` for the admin API (`/api/admin/*`): attaches the caller's
 * Supabase access token and, on a 401, refreshes the session once and retries.
 * Concurrent requests share a single refresh. Other failures are never retried.
 */
export function createAdminRequest(
  auth: SessionSource,
  fetchRequest: FetchLike,
  sessionErrorMessage: () => string,
): AdminRequest {
  let pendingRefresh: Promise<string> | null = null;

  const refreshAccessToken = (): Promise<string> => {
    pendingRefresh ??= (async () => {
      const { data, error } = await auth.refreshSession();
      const token = data?.session?.access_token;
      if (error || !token) throw new Error(sessionErrorMessage());
      return token;
    })().finally(() => {
      pendingRefresh = null;
    });
    return pendingRefresh;
  };

  return async (url, options = {}) => {
    const { data, error } = await auth.getSession();
    const token = data?.session?.access_token;
    if (error || !token) throw new Error(sessionErrorMessage());
    const send = (accessToken: string) => {
      const headers = new Headers(options.headers);
      headers.set("Authorization", `Bearer ${accessToken}`);
      return fetchRequest(url, { ...options, headers });
    };
    const response = await send(token);
    if (response.status !== 401) return response;
    // The admin API rejects unauthenticated calls before any write, so a retry is safe.
    const retried = await send(await refreshAccessToken());
    if (retried.status === 401) throw new Error(sessionErrorMessage());
    return retried;
  };
}
